import type { LayoutMode } from '@/hooks/useColorMoments';

/** Sanitize a hex color for safe interpolation into SVG/CSS strings. */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
function safeHex(color: string): string {
  return HEX_RE.test(color) ? color : '#000000';
}

interface ColorPaletteDisplayProps {
  colors: string[];
  layout: LayoutMode;
  className?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Renders a color palette with various layout modes.
 * Used in ColorMomentCard and the creation/preview screens.
 */
export function ColorPaletteDisplay({ colors, layout, className = '', children, onClick }: ColorPaletteDisplayProps) {
  const getLayoutClasses = () => {
    switch (layout) {
      case 'horizontal':
        return 'relative flex flex-col';
      case 'vertical':
        return 'relative flex flex-row';
      case 'grid':
        return colors.length === 6 ? 'relative grid grid-cols-3 grid-rows-2' : 'relative grid grid-cols-2 grid-rows-2';
      case 'star':
      case 'checkerboard':
      case 'diagonalStripes':
        return 'relative';
      default:
        return 'relative flex flex-col';
    }
  };

  const getStarStyle = (index: number, total: number) => {
    const angle = 360 / total;
    const startAngle = index * angle - 90;
    const scale = 1.5;
    const overlap = 0.5;
    const adjustedStartAngle = startAngle - overlap;
    const adjustedAngle = angle + overlap * 2;
    
    const points: string[] = ['50% 50%'];
    const steps = 12;
    for (let i = 0; i <= steps; i++) {
      const currentAngle = adjustedStartAngle + (adjustedAngle * i / steps);
      const rad = (currentAngle * Math.PI) / 180;
      const x = 50 + 50 * scale * Math.cos(rad);
      const y = 50 + 50 * scale * Math.sin(rad);
      points.push(`${x}% ${y}%`);
    }
    
    return {
      clipPath: `polygon(${points.join(', ')})`,
      width: '100%',
      height: '100%',
    };
  };

  const renderCheckerboard = () => {
    const n = colors.length;
    const cellSize = 1;
    const svgSize = n * cellSize;
    
    let rects = '';
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const color = safeHex(colors[(row + col) % n]);
        rects += `<rect x="${col * cellSize}" y="${row * cellSize}" width="${cellSize}" height="${cellSize}" fill="${color}"/>`;
      }
    }
    
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" shape-rendering="crispEdges">${rects}</svg>`;
    const encoded = encodeURIComponent(svg);
    
    // Make squares larger - repeat pattern 2x2 times (so each color block is 50%/n of the container)
    return (
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,${encoded}")`,
          backgroundSize: '50% 50%',
          backgroundRepeat: 'repeat',
          imageRendering: 'pixelated',
        }}
      />
    );
  };

  const renderDiagonalStripes = () => {
    const n = colors.length;
    // Use percentage-based stops for exactly n stripes across the diagonal
    const stripePercent = 100 / n;
    
    let stops = '';
    for (let i = 0; i < n; i++) {
      const start = i * stripePercent;
      const end = (i + 1) * stripePercent;
      const color = safeHex(colors[i]);
      if (i > 0) stops += ', ';
      stops += `${color} ${start}%, ${color} ${end}%`;
    }
    
    // Use linear-gradient (not repeating) so we get exactly n stripes
    return (
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${stops})`,
        }}
      />
    );
  };

  const renderColors = () => {
    if (layout === 'checkerboard') {
      return renderCheckerboard();
    }
    
    if (layout === 'diagonalStripes') {
      return renderDiagonalStripes();
    }
    
    if (layout === 'star') {
      return (
        <>
          {/* Background fill to prevent visible gap at center where all pie slices meet */}
          <div className="absolute inset-0" style={{ backgroundColor: colors[0] }} />
          {colors.map((color, index) => (
            <div
              key={index}
              className="absolute"
              style={{ 
                backgroundColor: color,
                ...getStarStyle(index, colors.length),
              }}
            />
          ))}
        </>
      );
    }
    
    return colors.map((color, index) => (
      <div
        key={index}
        className={
          layout === 'grid' ? '' :
          'flex-1'
        }
        style={{ 
          backgroundColor: color,
        }}
      />
    ));
  };

  return (
    <div className={`${getLayoutClasses()} ${className}`} onClick={onClick}>
      {renderColors()}
      {children}
    </div>
  );
}
